import { Signal } from '@angular/core';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_INVOICES = 25;

// Structural shape — `BillingInvoice` is a local interface in
// `settings-billing.ts`. We only need `id` and `number` here.
export interface BillingInvoiceLike {
  id: string;
  number?: string | null;
}

export interface BillingUsageMeterLike {
  id: string;
  labelKey: string;
  valueLabel: string;
  footLabel?: string | null;
}

export interface BillingAiPageContextSignals {
  readonly activeTab: Signal<string>;
  readonly invoices: Signal<readonly BillingInvoiceLike[]>;
  readonly usageMeters: Signal<readonly BillingUsageMeterLike[]>;
}

/**
 * Registers AI assistant page context for /settings/billing.
 *
 * - `visible_rows` = first 25 invoices on the invoices tab, or the compact
 *   usage meters visible on the plan tab.
 * - `filters.tab` = active tab (`plan` | `invoices`) so the assistant
 *   knows whether the user is viewing the plan/subscription or the
 *   invoice history.
 *
 * No `entity` — billing has no editable entity in scope (plan switches and
 * invoice downloads are transient actions, not persistent focus).
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideBillingAiPageContext(state: BillingAiPageContextSignals): void {
  providePageContext(() => {
    const tab = state.activeTab();

    return {
      visible_rows: visibleRowsForTab(state, tab),
      filters: {
        tab,
      },
    };
  });
}

function visibleRowsForTab(state: BillingAiPageContextSignals, tab: string) {
  if (tab === 'invoices') {
    return state
      .invoices()
      .slice(0, MAX_VISIBLE_INVOICES)
      .map((invoice) => ({
        type: 'invoice',
        id: invoice.id,
        label: invoice.number ?? invoice.id,
      }));
  }

  if (tab === 'plan') {
    return state.usageMeters().map((meter) => ({
      type: 'billing_usage_meter',
      id: meter.id,
      label: meter.footLabel
        ? `${meter.labelKey}: ${meter.valueLabel} (${meter.footLabel})`
        : `${meter.labelKey}: ${meter.valueLabel}`,
    }));
  }

  return [];
}
